



// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';

// Third party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Constants
import Assets from '@/constants/assets.ts';

// Components
import { FlatSelectorTextBorderBottom } from '@/shared/components/FlatSelectorText.component.vue';
import ConvocationRow, {
    ParticipantListType,
} from '@/manager/convocation/views/convocationDetail/convocationRow/convocationRow.view.vue';
import ButtonSwitch from '@/shared/components/button-switch.component.vue';
import DropUp, { DropUpColorHover } from '@/shared/components/DropUp.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import Spinner from '@/shared/components/Spinner.component.vue';
import TableColumnOrder from '@/shared/components/TableColumnOrder.component.vue';

// Models - Interfaces
import { Convocation } from '@/manager/convocation/convocation.model';
import { ConvocationService } from '@/manager/convocation/convocation.service';
import { Participant } from '@/manager/convocation/participant.model';

// Mixins
import WithSpinner from '@/shared/mixins/withSpinner.mixin.ts';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import SearchMixin from '@/shared/mixins/search.mixin.ts';

export enum ListActions {
    DownloadCsv = 'download-csv',
    EmailToMultiple = 'email-to-multiple',
}

interface MixinInterface extends WithPagination, SearchMixin, ColumnOrderMixin { }

@Component({
    components: {
        ConvocationRow,
        ButtonSwitch,
        DropUp,
        Icon,
        Pagination,
        Spinner,
        TableColumnOrder,
    },
    computed: {
        ...mapGetters({
            getConvocation: 'getConvocation',
            getPostponeList: 'getPostponeList',
            getRepeaterList: 'getRepeaterList',
            isOpenNavbar: 'isOpenNavbar',
            participantMassiveEmailList: 'getParticipantMassiveEmailList',
        }),
    },
})
export default class PostponeList extends Mixins<MixinInterface>(
    WithPagination,
    SearchMixin,
    ColumnOrderMixin,
) {
    @Prop() private convocationId!: string;
    @Prop() private isLive!: boolean;
    @Prop() private parsedNextConvocationListOptions!: any[];
    @Prop() private initialNextConvocationListIndex!: number;
    @Prop() private isBeforeConfirmationPeriod!: boolean;
    @Prop() private paymentStatusOptions!: any[];
    @Prop() private columnsVisibility!: any;
    @Prop() private searchValue!: string;
    @Prop() private searchNifValue!: string;
    @Prop() private headerClass!: string;

    private assets = Assets;
    private dropUpColorHover = DropUpColorHover;
    private iconEnum = IconEnum;
    private getConvocation!: any;
    private participantMassiveEmailList!: any;
    private isOpenNavbar!: any;
    private getPostponeList!: Participant[];
    private getRepeaterList!: Participant[];
    private listLoaded = false;
    private participantListType = ParticipantListType;
    private listSwitchSelected = this.listSwitchOptions[0];
    private flatSelectorTextBorderBottom = FlatSelectorTextBorderBottom;

    @Watch('searchValue')
    public watchSearchValue(newVal: any, oldVal: any) {
        this.searchString = this.searchValue;
    }

    @Watch('searchNifValue')
    public watchSearchNifValue(newVal: any, oldVal: any) {
        this.searchNifString = this.searchNifValue;
    }

    get convocation() {
        return this.getConvocation(this.convocationId);
    }

    get selectedList() {
        let list = this.getRepeaterList;
        if (this.listSwitchSelected.id === this.listSwitchOptions[0].id) {
            list = this.getPostponeList;
        }
        return list;
    }

    get convocationRowType() {
        let type = this.participantListType.RepeaterList;
        if (this.listSwitchSelected.id === this.listSwitchOptions[0].id) {
            type = this.participantListType.PostponeList;
        }
        return type;
    }

    get showPagination() {
        return this.listLoaded && !this.spinner.show;
    }

    get showTable() {
        const result = (this.listLoaded && !this.spinner.show);
        this.$emit('show-table', result);
        return result;
    }

    get lastColumnTitle() {
        return (this.isLive) ? 'Ent/Reg/Dip' : 'Ent/Dip';
    }

    get lastColumnClass() {
        return (this.isLive) ? 'table-cell--last-icons-convocation' : 'table-cell--last-icons-convocation--future';
    }

    get listSwitchOptions(): any[] {
        return [
            { id: 1, text: 'Asistentes' },
            { id: 2, text: 'Repetidores' },
        ];
    }

    get columnApName() {
        let name = 'Ap';
        if (this.isBeforeConfirmationPeriod) {
            name = 'CC';
        } else if (this.isLive) {
            name = 'Ab';
        }

        return name;
    }

    get dropUpWaitingListOptions() {
        return [
            { value: ListActions.DownloadCsv, name: 'Exportar lista' },
            { value: ListActions.EmailToMultiple, name: 'Email masivo' },
        ];
    }

    get showWaitingList() {
        return (this.listSwitchSelected.id === this.listSwitchOptions[0].id);
    }

    get showRepeaterList() {
        return (this.listSwitchSelected.id === this.listSwitchOptions[1].id);
    }

    get paginationPlural() {
        let plural = 'asistentes';
        if (this.showRepeaterList) {
            plural = 'repetidores';
        }
        return plural;
    }

    public searchCallback() {
        this.fetchFirstPageList();
    }

    public columnOrderFetchList() {
        this.requestShowSpinner();

        this.fetchParticipantList(
            (res: any) => {
                this.columnOrderOnFetchSuccess();
                this.changeSpinnerVisibility(false);
            },
        );
    }

    public fetchList() {
        this.requestShowSpinner();
        this.$emit('showing-spinner');
        this.fetchParticipantList((res: any) => {
            this.onFetchSuccess(res);
            this.$emit('list-loaded', res);
            this.listLoaded = true;
            this.changeSpinnerVisibility(false);
        });
    }

    private created() {
        this.searchString = this.searchValue;
        this.searchNifString = this.searchNifValue;
        this.fetchFirstPageList();
    }

    private refetchList() {
        this.$emit('refetch-list');
    }

    private fetchParticipantList(callback: any = () => { const doNothing = 1; }) {

        let action = 'fetchPostponeList';
        if (this.showRepeaterList) {
            action = 'fetchRepeaterList';
        }

        this.$store.dispatch(
            action,
            {
                id: this.convocationId,
                offset: this.offset,
                limit: this.limit,
                search: this.searchString,
                searchNif: this.searchNifString,
                ordering: this.composeOrdering(),
            },
        ).then((res: any) => {
            this.changeSpinnerVisibility(false);
            if (callback) {
                callback(res);
            }
        })
            .catch((err) => {
                this.changeSpinnerVisibility(false);
            });
    }

    private getRequestParams() {
        let params = {};
        if (this.showRepeaterList) {
            params = { postpone_repeater_list: true };
        } else {
            params = { pending_postpone: true };
        }
        return params;
    }

    private fetchParticipantMassiveEmailList(callback: any = () => { const doNothing = 1; }) {
        const params = {
            search: this.searchString,
            team_list: false,
            pending_postpone: true,
            // waiting_repeater_list: this.showRepeaterList,
        };
        this.$store.dispatch('fetchParticipantMassiveEmail', {
            convocationId: this.convocationId,
            params,
        }).then(
            (res) => {
                callback();
            },
        );
    }

    private dropUpOptionSelected(option: any) {
        switch (option.value) {
            case ListActions.DownloadCsv:
                ConvocationService.downloadCsv(this.convocationId, this.getRequestParams());
                break;
            case ListActions.EmailToMultiple:
                this.fetchParticipantMassiveEmailList(
                    () => {
                        ConvocationService.mailToFilteredUsers(this.participantMassiveEmailList);
                    },
                );
                break;
        }
    }

    private listSwitchChange(option: any) {
        this.listSwitchSelected = option;
        this.fetchFirstPageList();
    }

    private showMessage(message: string) {
        this.$emit('show-message', message);
    }

}
